import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { patchItem } from '../../../shared/api/requestItems.api'
import { toast } from '../../../shared/ui/toastStore'
import { patchItemInCache } from '../utils/patchItemInCache'
import { assigneeLabel, useAssignableBuyers } from '../hooks/useAssignableBuyers'
import { assignWithConfirm } from '../hooks/useBulkAssignProcurement'

type Person = { id: string; name?: string | null; email: string }

/**
 * Выпадашка «Назначен» по позиции в карточке заявки.
 *
 * Продажи (ADMIN/MANAGER/ROP/ASSISTANT) — правка одной позиции (PATCH), в списке
 * есть пункт «(менеджер)» — менеджер заявки. РОЗ (№50, 29.09.2026) — `bulk`:
 * себя и свою команду, без «(менеджер)», сохранение через bulk-assign с тем же
 * предупреждением о живой работе закупщика, что и у массового назначения.
 * Список назначаемых — `useAssignableBuyers` (закупщики и РОЗ).
 */
export function AssignSelect({
  itemId,
  currentAssignedId,
  currentAssignedLabel,
  requestId,
  requestManager,
  bulk = false,
}: {
  itemId: string
  currentAssignedId: string | null
  /** Кто назначен сейчас — если его нет в списке (менеджер, заблокированный). */
  currentAssignedLabel?: string | null
  requestId: string
  requestManager?: Person | null
  bulk?: boolean
}) {
  const queryClient = useQueryClient()
  const buyers = useAssignableBuyers()
  const [saving, setSaving] = useState(false)
  const manager = bulk ? null : (requestManager ?? null)

  const options = buyers.filter((pu) => pu.id !== manager?.id)
  const currentMissing =
    !!currentAssignedId &&
    currentAssignedId !== manager?.id &&
    !options.some((pu) => pu.id === currentAssignedId)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value || null
    setSaving(true)
    try {
      if (bulk) {
        const res = await assignWithConfirm(requestId, [itemId], val)
        if (!res) return
        if (res.skipped > 0) {
          toast.error('Назначать можно только позиции с матрицей «Китай»/«Производители»')
          return
        }
      } else {
        await patchItem(itemId, { assignedToId: val })
      }
      // Локально обновляем и assignedToId, и assignedTo, чтобы AG Grid
      // мгновенно показал нового назначенного без рефетча всей заявки.
      const picked: Person | undefined =
        manager?.id === val ? manager : buyers.find((pu) => pu.id === val)
      patchItemInCache(queryClient, requestId, itemId, {
        assignedToId: val,
        assignedTo: picked
          ? { id: picked.id, name: picked.name ?? null, email: picked.email }
          : null,
      })
      queryClient.invalidateQueries({ queryKey: ['requests', requestId], refetchType: 'none' })
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось назначить'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <select
      value={currentAssignedId ?? ''}
      onChange={handleChange}
      disabled={saving}
      aria-label="Назначен"
      className="w-full text-xs border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-50 cursor-pointer hover:border-brand-300 transition-colors"
    >
      <option value="">— не назначен —</option>
      {currentMissing && (
        <option value={currentAssignedId} disabled>
          {currentAssignedLabel || 'назначен'}
        </option>
      )}
      {manager && (
        <option key={manager.id} value={manager.id}>
          {manager.name || manager.email} (менеджер)
        </option>
      )}
      {options.map((pu) => (
        <option key={pu.id} value={pu.id}>
          {assigneeLabel(pu)}
        </option>
      ))}
    </select>
  )
}
